import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { ProgressButton, useToast } from "@qubit/autoparts";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import useFlag from "~/config/flags";
import {
  BulkCleanBinsProgressState,
  BULK_CLEAN_BINS_CLEANING_FLAGS,
  BULK_CLEAN_BINS_HOLD_FLAGS
} from "~/features/preview/bins/binSettings.types";
import {
  countBinsWithCleaningFlags,
  countSelectedFlagsToRemove,
  createDefaultSelectedFlagsArray,
  filterBinsBySelectedFlags,
  getCleanableFlaggedBins,
  getFlagCountsByType,
  getSelectedFlagsForBin,
  removeBinFlags,
  selectedFlagsIncludeCleaningFlag,
  translationKeyForCount
} from "~/features/preview/bins/binSettings.utils";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import {
  useDeleteBinFlagMutation,
  useGetBinCleaningReportQuery,
  useGetFlaggedBinsQuery
} from "~/redux/warehouse/autostoreGrid.hooks";
import { FlaggedReason } from "~/redux/warehouse/bins.openApi";
import { warehouseApi } from "~/redux/warehouse/warehouseApi";
import { BinMaintenanceAssignment } from "~/types/api";

import { BulkCleanBinFlagChips } from "./BulkCleanBinFlagChips";
import { BulkCleanBinsProgress } from "./BulkCleanBinsProgress";

type BulkCleanBinsModalProps = {
  open: boolean;
  onClose: () => void;
  autostoreGridId: string | undefined;
};

export function BulkCleanBinsModal(props: BulkCleanBinsModalProps) {
  const { open, onClose, autostoreGridId } = props;
  const { t } = useTranslation();
  const { enableColorChanges } = useFlag();
  const dispatch = useAppDispatch();
  const { successToast, errorToast } = useToast();
  const siteWorkstation = useAppSelector(selectThisWorkstation);

  const [binCountInput, setBinCountInput] = useState("1");
  const [selectedFlags, setSelectedFlags] = useState<FlaggedReason[]>([]);
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const [progress, setProgress] = useState<BulkCleanBinsProgressState | null>(
    null
  );

  const isProcessing = progress != null;

  const [deleteBinFlag] = useDeleteBinFlagMutation();

  const shouldFetchModalData = open && !!autostoreGridId && !isProcessing;

  const { data: flaggedBins, isFetching: isFetchingFlaggedBins } =
    useGetFlaggedBinsQuery(
      {
        autostoreGridId: autostoreGridId!,
        workstationId: undefined
      },
      { skip: !shouldFetchModalData }
    );

  const { data: binCleaningReport, isFetching: isFetchingCleaningReport } =
    useGetBinCleaningReportQuery(autostoreGridId!, {
      skip: !shouldFetchModalData
    });

  const isFetching = isFetchingFlaggedBins || isFetchingCleaningReport;

  const cleanableBins = useMemo(
    () => getCleanableFlaggedBins(flaggedBins),
    [flaggedBins]
  );

  const flagCounts = useMemo(
    () => getFlagCountsByType(cleanableBins),
    [cleanableBins]
  );

  const binsWithCleaningFlags = useMemo(
    () => countBinsWithCleaningFlags(cleanableBins),
    [cleanableBins]
  );

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!open) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setHasInitializedSelection(false);
      return;
    }

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (hasInitializedSelection || isFetchingFlaggedBins) {
      return;
    }

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
    setSelectedFlags(createDefaultSelectedFlagsArray(cleanableBins));
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
    setHasInitializedSelection(true);
  }, [cleanableBins, hasInitializedSelection, isFetchingFlaggedBins, open]);

  const selectedFlagSet = useMemo(
    () => new Set(selectedFlags),
    [selectedFlags]
  );

  const matchingBins = useMemo(
    () => filterBinsBySelectedFlags(cleanableBins, selectedFlagSet),
    [cleanableBins, selectedFlagSet]
  );

  const parsedBinCount = Number(binCountInput);
  const binCount =
    Number.isFinite(parsedBinCount) && parsedBinCount > 0
      ? Math.floor(parsedBinCount)
      : 0;

  const maxProcessableBins = matchingBins.length;
  const willAffectTotalToClean =
    selectedFlagsIncludeCleaningFlag(selectedFlagSet);

  const hasFlaggedBins = cleanableBins.length > 0;
  const isFullyBlocked = !isFetching && !hasFlaggedBins;
  const showCountInput = !isFetching && maxProcessableBins > 0;

  const binsToProcess = useMemo(
    () => matchingBins.slice(0, binCount),
    [binCount, matchingBins]
  );

  const canConfirm =
    selectedFlags.length > 0 &&
    binsToProcess.length > 0 &&
    !isProcessing &&
    !!autostoreGridId;

  const readySummary = useMemo(() => {
    if (isFetching || isFullyBlocked || !showCountInput) {
      return null;
    }
    const count = binsToProcess.length;
    const formattedCount = count.toLocaleString();
    const binLabel = t(
      translationKeyForCount("bin", "bins", count)
    ).toLowerCase();
    if (willAffectTotalToClean) {
      return `${t("bulk clean bins will clean")} ${formattedCount} ${binLabel} · ${t("bulk clean bins to clean drops by")} ${formattedCount}`;
    }
    return `${t("bulk clean bins will clear flags on")} ${formattedCount} ${binLabel}`;
  }, [
    binsToProcess.length,
    isFetching,
    isFullyBlocked,
    showCountInput,
    t,
    willAffectTotalToClean
  ]);

  const resetAndClose = () => {
    setBinCountInput("1");
    setSelectedFlags([]);
    setHasInitializedSelection(false);
    setProgress(null);
    onClose();
  };

  const handleClose = () => {
    if (isProcessing) return;
    resetAndClose();
  };

  const handleToggleFlag = (flag: FlaggedReason) => {
    setSelectedFlags((current) =>
      current.includes(flag)
        ? current.filter((value) => value !== flag)
        : [...current, flag]
    );
  };

  const handleBulkCleanBins = async () => {
    if (!canConfirm || !autostoreGridId) return;

    const binsSnapshot: BinMaintenanceAssignment[] = [...binsToProcess];
    const flagsSnapshot = new Set(selectedFlags);
    const totalFlags = countSelectedFlagsToRemove(binsSnapshot, flagsSnapshot);

    setProgress({
      totalBins: binsSnapshot.length,
      remainingBins: binsSnapshot.length,
      totalFlags,
      remainingFlags: totalFlags,
      currentBinId: null
    });

    let processedBinCount = 0;
    let removedFlagCount = 0;

    try {
      for (const bin of binsSnapshot) {
        const flagsForBin = getSelectedFlagsForBin(bin, flagsSnapshot);
        if (flagsForBin.length === 0) {
          continue;
        }

        setProgress((current) =>
          current
            ? {
                ...current,
                currentBinId: bin.binId
              }
            : current
        );

        await removeBinFlags(deleteBinFlag, {
          autostoreGridId,
          binNumber: bin.binId,
          flags: flagsForBin,
          workstationId: siteWorkstation?.id,
          deferCacheInvalidation: true
        });
        processedBinCount += 1;
        removedFlagCount += flagsForBin.length;

        setProgress((current) =>
          current
            ? {
                ...current,
                remainingBins: current.remainingBins - 1,
                remainingFlags: current.remainingFlags - flagsForBin.length,
                currentBinId: null
              }
            : current
        );
      }

      dispatch(
        warehouseApi.util.invalidateTags([
          "flagged bins",
          "bin cleaning report",
          "bin maintenance"
        ])
      );

      const binLabel = t(
        translationKeyForCount("bin", "bins", processedBinCount)
      ).toLowerCase();
      const flagLabel = t(
        translationKeyForCount("flag", "flags", removedFlagCount)
      ).toLowerCase();

      successToast(
        willAffectTotalToClean
          ? t("bulk clean bins success toast")
          : t("bulk clean bins success toast flags only"),
        {
          description: willAffectTotalToClean
            ? `${t("bulk clean bins toast cleaned")} ${processedBinCount.toLocaleString()} ${binLabel}.`
            : `${t("bulk clean bins toast cleared")} ${removedFlagCount.toLocaleString()} ${flagLabel} ${t("bulk clean bins toast from")} ${processedBinCount.toLocaleString()} ${binLabel}.`
        }
      );
      resetAndClose();
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
      if (processedBinCount > 0) {
        dispatch(
          warehouseApi.util.invalidateTags([
            "flagged bins",
            "bin cleaning report",
            "bin maintenance"
          ])
        );
      }
      setProgress(null);
    }
  };

  const remainingBinsToClean = binCleaningReport?.remainingBinsToClean ?? 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-start"
        },
        "& .MuiDialog-paper": {
          mt: 4,
          maxHeight: "calc(100vh - 64px)"
        }
      }}
    >
      <DialogTitle>{t("bulk clean bins modal title")}</DialogTitle>
      <DialogContent dividers>
        {isProcessing && progress ? (
          <BulkCleanBinsProgress progress={progress} />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {isFetching ? (
              <Typography variant="body2">{t("loading")}</Typography>
            ) : (
              <>
                <Stack spacing={0.5}>
                  <Typography variant="h6" component="p">
                    {remainingBinsToClean.toLocaleString()}{" "}
                    {t("bulk clean bins to clean suffix")}
                  </Typography>
                  {binsWithCleaningFlags > 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {binsWithCleaningFlags.toLocaleString()}{" "}
                      {t("bulk clean bins ready to clean")}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("bulk clean bins no cleaning flags ready")}
                    </Typography>
                  )}
                </Stack>

                {isFullyBlocked ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("bulk clean bins blocked none flagged")}
                  </Typography>
                ) : (
                  <>
                    <Stack spacing={1}>
                      <Stack spacing={0.25}>
                        <Typography variant="subtitle2">
                          {t("bulk clean bins cleaning flags title")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t("bulk clean bins cleaning flags hint")}
                        </Typography>
                      </Stack>
                      <BulkCleanBinFlagChips
                        flags={BULK_CLEAN_BINS_CLEANING_FLAGS}
                        selectedFlags={selectedFlagSet}
                        flagCounts={flagCounts}
                        onToggleFlag={handleToggleFlag}
                      />
                    </Stack>

                    <Accordion
                      disableGutters
                      elevation={0}
                      sx={{ bgcolor: "transparent" }}
                      defaultExpanded={binsWithCleaningFlags === 0}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack spacing={0.25}>
                          <Typography variant="subtitle2">
                            {t("bulk clean bins hold flags title")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t("bulk clean bins hold flags hint")}
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <BulkCleanBinFlagChips
                          flags={BULK_CLEAN_BINS_HOLD_FLAGS}
                          selectedFlags={selectedFlagSet}
                          flagCounts={flagCounts}
                          onToggleFlag={handleToggleFlag}
                        />
                      </AccordionDetails>
                    </Accordion>

                    {!showCountInput && binsWithCleaningFlags > 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {t("bulk clean bins pick flags hint")}
                      </Typography>
                    ) : null}

                    {showCountInput ? (
                      <>
                        <TextField
                          label={
                            willAffectTotalToClean
                              ? t("bulk clean bins step count label")
                              : t("bulk clean bins step count label holds")
                          }
                          helperText={`${maxProcessableBins.toLocaleString()} ${t("bulk clean bins available suffix")}`}
                          type="number"
                          size="small"
                          value={binCountInput}
                          onChange={(e) => setBinCountInput(e.target.value)}
                          inputProps={{
                            min: 1,
                            max: maxProcessableBins,
                            "aria-label": willAffectTotalToClean
                              ? t("bulk clean bins step count label")
                              : t("bulk clean bins step count label holds")
                          }}
                        />

                        {readySummary ? (
                          <Typography variant="body2" fontWeight={600}>
                            {readySummary}
                          </Typography>
                        ) : null}
                      </>
                    ) : null}
                  </>
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {!isProcessing ? (
          <>
            <Button
              onClick={handleClose}
              color={enableColorChanges ? "secondary" : "primary"}
            >
              {t("cancel")}
            </Button>
            {!isFullyBlocked ? (
              <ProgressButton
                color="error"
                variant="contained"
                onClick={() => void handleBulkCleanBins()}
                disabled={!canConfirm}
                aria-label={
                  willAffectTotalToClean
                    ? t("clean bins")
                    : t("bulk clean bins clear flags")
                }
              >
                {willAffectTotalToClean
                  ? t("clean bins")
                  : t("bulk clean bins clear flags")}
              </ProgressButton>
            ) : null}
          </>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
